// src/pages/Acoes/components/ConcluirPlanoDialog.jsx
import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { concluirPlanoAcao } from '../../../infra/api/PlanosAcao';
import { buscarColetas, mensagemErro } from '../../../infra/api/Coletas';
import { deInputDataHora, formatarContagem, formatarDataHora, paraInputDataHora } from '../../../utils/coleta';

const AUTOMATICO = '__automatico__';

/**
 * Registrar conclusão: quando foi executada e o que foi feito. A amostra de verificação
 * ("depois") é automática — a primeira do mesmo ponto, origem e turno coletada depois da
 * execução — e pode ser trocada por outra do mesmo ponto.
 */
export default function ConcluirPlanoDialog({ open, onClose, item, onSalvo }) {
    const [execucao, setExecucao] = useState('');
    const [registro, setRegistro] = useState('');
    const [depois, setDepois] = useState(AUTOMATICO);
    const [candidatas, setCandidatas] = useState([]);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');
    const antes = item?.antes;

    useEffect(() => {
        if (!open) return;
        setExecucao(paraInputDataHora(new Date()));
        setRegistro('');
        setDepois(AUTOMATICO);
        setErro('');
        setCandidatas([]);
        if (antes?.pagina && antes?.pontoColetaId) {
            buscarColetas({ pagina: antes.pagina, pontoColetaId: antes.pontoColetaId }, 0, 20)
                .then((r) =>
                    setCandidatas(
                        (r.items || []).filter(
                            (c) => c.id !== antes.amostraId && Number.isFinite(c.experimento?.log10Contagem)
                        )
                    )
                )
                .catch(() => setCandidatas([]));
        }
    }, [open, antes]);

    const salvar = async () => {
        const quando = deInputDataHora(execucao);
        if (!quando) {
            setErro('Informe quando a ação foi executada.');
            return;
        }
        if (!registro.trim()) {
            setErro('Descreva o que foi feito.');
            return;
        }
        setSalvando(true);
        setErro('');
        try {
            const salvo = await concluirPlanoAcao(item.plano.id, {
                execucaoEm: quando.toISOString(),
                registro: registro.trim(),
                amostraDepoisId: depois === AUTOMATICO ? null : depois,
            });
            onSalvo(salvo);
        } catch (e) {
            setErro(mensagemErro(e, 'Não foi possível registrar a conclusão.'));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Dialog open={open} onClose={salvando ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ pr: 6 }}>
                Registrar conclusão
                <Typography variant="body2" color="text.secondary">{item?.plano?.titulo}</Typography>
                <IconButton onClick={onClose} disabled={salvando} aria-label="Fechar" sx={{ position: 'absolute', right: 12, top: 12 }}>
                    <Lucide.X size={18} />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {erro && <Alert severity="error">{erro}</Alert>}
                    <TextField
                        label="Executada em"
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                        value={execucao}
                        onChange={(e) => setExecucao(e.target.value)}
                    />
                    <TextField
                        label="O que foi feito"
                        placeholder="Ex.: Rotina revisada e nova amostra coletada."
                        multiline
                        minRows={3}
                        value={registro}
                        onChange={(e) => setRegistro(e.target.value)}
                        inputProps={{ maxLength: 1000 }}
                        required
                    />
                    {antes && (
                        <TextField
                            select
                            label="Amostra de verificação (depois)"
                            sx={{ '& .MuiSelect-select.MuiSelect-select': { whiteSpace: 'normal' } }}
                            value={depois}
                            onChange={(e) => setDepois(e.target.value)}
                            helperText="Automática: a primeira amostra do mesmo ponto, origem e turno coletada depois da execução."
                        >
                            <MenuItem value={AUTOMATICO}>Automática (recomendado)</MenuItem>
                            {candidatas.map((c) => (
                                <MenuItem key={c.id} value={c.id}>
                                    {c.identificacao} · {formatarDataHora(new Date(c.coleta?.dataHora))} · {formatarContagem(c.experimento.log10Contagem)}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={salvando}>Cancelar</Button>
                <Button variant="contained" disableElevation onClick={salvar} disabled={salvando} sx={{ borderRadius: 2 }}>
                    {salvando ? 'Salvando…' : 'Registrar conclusão'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
